import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  ViewChild
} from '@angular/core';
import {MatFormField, MatInput, MatLabel} from "@angular/material/input";
import {MatOption, MatSelect, MatSelectChange} from "@angular/material/select";
import {SelectData} from "../../../../core/types/records-table.types";
import {MoveDirection} from "../../../../core/types/move-direction.types";
import {FormsModule} from "@angular/forms";
import {CustomMatSelectService} from "./services/custom-mat-select.service";
import {selectDataTransform} from "./configs/custom-mat-select-config";
import {UtilityService} from "../../../../core/services/utility.service";

@Component({
  selector: 'app-custom-mat-select',
  imports: [
    MatFormField,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
    FormsModule
  ],
  templateUrl: './custom-mat-select.component.html',
  styleUrl: './custom-mat-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomMatSelectComponent implements OnChanges {
  @Input() label: string = '';
  @Input() selectedValueId: string = '';
  @Input() needEmptyValue: boolean = true;
  @Input({ required: true, transform: selectDataTransform }) options: Array<SelectData> = [];
  @Input() fullWidth: boolean = false;
  @Input() isEditModeActive: boolean = true;
  @Output() selectedChange: EventEmitter<string> = new EventEmitter<string>();
  @ViewChild('matSelect') matSelect!: MatSelect;
  @ViewChild('inputElement') inputElement: ElementRef<HTMLInputElement> | null = null;

  private customMatSelectService: CustomMatSelectService = inject(CustomMatSelectService);
  private utilityService: UtilityService = inject(UtilityService);

  public searchTerm: string = '';
  public isEmptyOptions: boolean = false;
  public selectedIndex: number = 0;
  public selectedValue: string = '';

  public ngOnChanges(): void {
    if (this.options.length && this.selectedValueId) {
      this.selectedValue = this.options.find(i => String(i.id) === this.selectedValueId)?.value || '';
    }
  }

  public handleSearchTermChange(value: string): void {
    this.searchTerm = value;
    this.getSelectData();
  }

  public async handleSelectClick(): Promise<void> {
    this.matSelect.open();
  }

  public handleKeyEvent(event: KeyboardEvent) {
    const handlers: Record<string, () => void> = {
      ArrowUp: () => this.handleArrowKey(event, 'up'),
      ArrowDown: () => this.handleArrowKey(event, 'down'),
      Enter: () => this.handleEnter(event)
    };

    handlers[event.key]?.();
  }

  public handleSelectionChange({ value }: MatSelectChange): void {
    this.searchTerm = '';
    this.selectedValueId = value;
    this.selectedValue = this.options.find(i => i.id === value)?.value || '';
    this.selectedChange.emit(value);
  }

  public getSelectData(): Array<SelectData> {
    if (!this.options.length) { return []; }

    const filteredOptions = this.options.filter((option: SelectData) => {
      return option.display && option.value.toLowerCase().includes(this.searchTerm.toLowerCase());
    });

    this.isEmptyOptions = !filteredOptions.length;
    return filteredOptions;
  }

  public getColorWithOpacity(hex: string | null, opacity: number): string {
    return !hex ? '#fff' : this.utilityService.hexToRgba(hex, opacity);
  }

  public resetToValue(value: string): void {
    this.selectedValueId = value;
    this.selectedValue = this.options.find(i => String(i.id) === value)?.value || '';
    this.searchTerm = '';
    this.matSelect.value = value;
  }

  public getClasses(): string {
    const fullWidth = this.fullWidth ? 'full-width' : '';
    return this.isEditModeActive ? `${fullWidth}` : `${fullWidth} none-pointer-events`;
  }

  private handleArrowKey(event: Event, direction: MoveDirection): void {
    event.preventDefault();
    const step = direction === 'up' ? -1 : 1;
    this.moveSelection(step);
  }

  private handleEnter(event: Event): void {
    event.preventDefault();
    const value = this.getSelectData().find(i => i.id === this.selectedIndex)?.id || '';
    this.matSelect?.close();
    this.inputElement?.nativeElement.blur();
    this.handleSelectionChange({ value: value } as MatSelectChange);
  }

  private moveSelection(step: number): void {
    const options = this.getSelectData();
    if (!options.length) { return; }

    const currentIndex = options.findIndex(option => option.id === this.selectedIndex);
    const newIndex = currentIndex === -1 ?
      options.length - 1 :
      (currentIndex + step + options.length) % options.length;

    this.selectedIndex = options[newIndex].id;
    this.customMatSelectService.scrollSelectedOptionIntoView(newIndex);
  }
}
